<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      ref="tableRemoteRef"
      :iconHeader="'add'"
      :items="displayedTypes"
      :headers="tableHeaders"
      :disablePagination="true"
      :title="$t('productCategories.pageName')"
      :pagination="pagination"
      :centerColumns="['action']"
      :sortableColumns="['name', 'orderNumber']"
      :notSortableColumns="['action']"
      hideDefaultFooter
      :isSearchable="false"
      @iconAction="onClickAction"
      @onSearchInput="onSearchInput"
      @sendQueryParams="fetchQuery"
      @search="debouncedSendQueryParams"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('sites.placeholder.searchBy')"
          :filterName="'name'"
          @onSearch="handleSearch"
        />
      </template>

      <template #body="{ items }">
        <draggable
          :list="items"
          :options="{ disabled: isDraggingDisabled }"
          tag="tbody"
          @change="onDragChange"
        >
          <tr
            v-for="(item, index) in items"
            :key="index"
          >
            <template v-for="(headerN, indexN) in tableHeaders">
              <td
                v-if="headerN.value === 'orderNumber'"
                :key="`${index}_${indexN}_drag`"
              >
                <template>
                  <v-icon
                    v-if="!isDraggingDisabled"
                    large
                    class="hidden-md-and-down"
                    color="primary"
                  >
                    mdi-drag-vertical
                  </v-icon>
                  <span
                    @click="
                      activateTooltip(item.displayOrder)
                    "
                  >
                    {{ item.displayOrder }}
                  </span>
                </template>
              </td>
              <td
                v-else-if="headerN.value === 'action'"
                :key="`${index}_${indexN}_action`"
              >
                <template v-if="itemActions">
                  <div class="action-buttons">
                    <VTableAction
                      :itemActions="itemActions"
                      :verticalActionDots="true"
                      :isCustomIcon="true"
                      @iconAction="
                        (action) =>
                          onClickAction(item, action)
                      "
                    />
                  </div>
                </template>
              </td>
              <td
                v-else-if="headerN.value === 'name'"
                :key="`${index}_${indexN}_others`"
              >
                <span>{{ item[headerN.value] }}</span>
                <div
                  v-if="isTooltipActive(item.displayOrder)"
                  class="custom-drag-input"
                />
              </td>
            </template>
          </tr>
        </draggable>
      </template>
    </TableRemote>
    <div
      v-intersect="endIntersect"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep, isPlainObject, debounce } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableAction from '@/components/shared/widgets/VTableAction';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import draggable from 'vuedraggable';
import perpage from '@/constants/pagination';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ProductCategoryForm from './ProductCategoryForm.vue';


export default {
    components: {
        TableRemote,
        VTableAction,
        draggable,
        SearchWithFilters,
    },
    data() {
        return {
            productCategories: [],
            loading: false,
            isFetching: false,
            isDraggingDisabled: false,
            pagination: {},

            customDragItemPosition: 0,
            searchInput: '',
            queryParams: {
                perpage,
                page: 1,
            },
            debouncedSendQueryParams: debounce(this.fetchQuery, 500),
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'hasRole',
            'teamMainLang',
            'teamLangs',
        ]),
        tableHeaders() {
            return [
                {
                    text: '',
                    value: 'orderNumber',
                    sortable: '',
                },
                {
                    text: this.$t('forms.labels.name'),
                    value: 'name',
                    width: '84%',
                    sortable: true,
                },
                {
                    text: this.$t('controls.actions'),
                    value: 'action',
                    sortable: false,
                },
            ];
        },
        displayedTypes() {
            return this.productCategories.map(category => {
                const { id, name, displayOrder } = category;
                const categoryLabel = isPlainObject(category)
                    ? category.name[this.userLang]
                    : '-';
                return {
                    id,
                    name: name[this.userLang] && name[this.userLang].length
                        ? name[this.userLang] : name[this.teamMainLang],
                    displayOrder,
                    category: categoryLabel,
                };
            });
        },
        itemActions() {
            const actions = [];
            if (this.hasPermission('products.categories.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }
            if (this.hasPermission('products.categories.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getProductCategories();
        this.loading = false;
    },
    methods: {

        async getProductCategories(nextPage = 1) {
            if (this.isFetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getProductCategories(this.queryParams);
                this.productCategories = [...data.categories];
                this.pagination = data.pagination;
                this.isFetching = false;
            }
            if (nextPage <= this.queryParams.page) {
                return;
            }
            this.isFetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getProductCategories(this.queryParams);
            this.productCategories = [...this.productCategories, ...data.categories];
            this.pagination = data.pagination;
            this.isFetching = false;
        },

        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('productCategories.add'),
                component: Vue.component(
                    'ProductCategoryForm',
                    ProductCategoryForm,
                ),
                showLangs: true,
                isForm: true,
                onConfirm: requestPayload => this.addCategory(requestPayload),
            });
        },
        isTooltipActive(currentIndex) {
            return this.showTooltipIndex === currentIndex;
        },
        activateTooltip(currentIndex) {
            this.showTooltipIndex = currentIndex;
            this.customDragItemPosition = currentIndex;
        },
        async onSearchInput(inputVal) {
            this.searchInput = inputVal;
            if (inputVal.length) {
                this.isDraggingDisabled = true;
            }
            await this.getProductCategories();
        },

        async onDragChange(e) {
            const { element: category, newIndex } = e.moved;
            await this.$rq.updateProductCategoryPosition(category.id, {
                newIndex,
            });
            await this.getProductCategories();
        },
        async onClickAction(category, action) {
            if (!action && category === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getProductCategory(category.id);
                this.$root.$emit('showModal', {
                    title: this.$t('productCategories.update'),
                    component: Vue.component(
                        'ProductCategoryForm',
                        ProductCategoryForm,
                    ),
                    payload: {
                        category: cloneDeep(data),
                        isEdition: true,
                    },
                    showLangs: true,
                    isForm: true,
                    onConfirm: () => this.updateCategory(category.id, data),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('productCategories.delete'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('productCategories.confirm') },
                    onConfirm: () => this.removeCategory(category.id),
                    isForm: false,
                });
            }
        },
        async addCategory(requestPayload) {
            await this.$rq.addProductCategory({ name: requestPayload });
            this.$toasted.global.toast_success({
                message: this.$t('productCategories.added'),
            });
            await this.getProductCategories();
        },
        async updateCategory(id, requestPayload) {
            await this.$rq.updateProductCategory(id, { name: requestPayload.name });
            this.$toasted.global.toast_success({
                message: this.$t('productCategories.updated'),
            });
            await this.getProductCategories();
        },
        async removeCategory(id) {
            await this.$rq.removeProductCategory(id);
            this.$toasted.global.toast_success({
                message: this.$t('productCategories.deleted'),
            });
            await this.getProductCategories();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getProductCategories(this.queryParams.page + 1);
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        handleSearch(search) {
            this.queryParams.search = search;
            this.getProductCategories();
        },
        async fetchQuery({ ...params }) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
            };
            await this.getProductCategories();
        },
    },
};
</script>
<style lang="scss" scoped>
.custom-drag-input {
    position: relative;
    width: 18%;
    .wrapper-div {
        position: absolute;
        top: 8px;
        height: 33px;
        padding: 0 5px;
        outline: 1px solid;
        background-color: white;
        .v-text-field {
            padding-top: 0;
            margin-top: 0;
        }
    }
}
</style>
